import { useCallback, useEffect, useRef, useState } from 'react';

// Activity that counts as "the user is here" — reset by any of these on
// `window`, and by `extend()`. Deliberately not `click`: a click is covered
// by `pointerdown` already firing first, and we don't want a double reset.
// `scroll` doesn't bubble, so a plain bubble-phase listener on `window`
// never sees a scroll inside an inner container (a V2 AppShell panel, the
// quotes modal) — it needs `capture: true` to catch it on the way down.
const ACTIVITY_EVENTS = ['mousemove', 'keydown', 'pointerdown', 'scroll', 'touchstart'];
const CAPTURE_EVENTS = new Set(['scroll']);

const TICK_MS = 1000;

/**
 * Idle-timeout hook: warns, then signs out, after a period of no activity.
 *
 * Pure of Amplify and React Router — it only knows about `window`/`document`
 * events and the callbacks it's given. `App.js` wires the actual sign-out
 * in a later task.
 *
 * Background tabs throttle `setInterval`, so the 1-second tick is a
 * best-effort clock, not the source of truth: `visibilitychange` (to
 * visible) and `focus` both recompute from the wall clock (`now()`)
 * immediately, so a tab left hidden past the deadline signs out the instant
 * it's shown again rather than waiting for a throttled timer to catch up.
 *
 * @param {object} opts
 * @param {number} opts.timeoutMs - idle time before `onTimeout` fires.
 * @param {number} opts.warnMs - idle time before `onWarn` fires (< timeoutMs).
 * @param {() => void} [opts.onWarn]
 * @param {() => void} [opts.onTimeout]
 * @param {() => void} [opts.onRevalidate] - called after every focus/visibility recompute.
 * @param {boolean} [opts.enabled=true] - false installs nothing and never fires.
 * @param {() => number} [opts.now=Date.now]
 * @returns {{ extend: () => void, warned: boolean, remainingMs: number }}
 */
export function useIdleTimeout({
  timeoutMs,
  warnMs,
  onWarn,
  onTimeout,
  onRevalidate,
  enabled = true,
  now = Date.now,
}) {
  // Latest-value ref so the effect below doesn't need these in its deps —
  // callbacks in particular are rarely memoized by callers, and listing them
  // would tear down and reinstall every listener on every render.
  const optsRef = useRef(null);
  optsRef.current = { timeoutMs, warnMs, onWarn, onTimeout, onRevalidate, now };

  const [warned, setWarned] = useState(false);
  const [remainingMs, setRemainingMs] = useState(timeoutMs);

  // Seeded with a placeholder — the install effect below sets the real
  // value from `now()` before anything can read it, so this never calls
  // `now()` on every render just to discard the result.
  const lastActivityRef = useRef(0);
  const warnedRef = useRef(false);
  const doneRef = useRef(false);
  const intervalRef = useRef(null);

  // Recomputes idle state from the wall clock. Used by the 1-second tick
  // and, directly, by the focus/visibilitychange handlers.
  const recompute = useCallback(() => {
    if (doneRef.current) return;
    const { timeoutMs: timeout, warnMs: warn, onWarn: handleWarn, onTimeout: handleTimeout, now: getNow } =
      optsRef.current;
    const idle = getNow() - lastActivityRef.current;
    setRemainingMs(Math.max(0, timeout - idle));

    if (idle >= warn && !warnedRef.current) {
      warnedRef.current = true;
      setWarned(true);
      if (handleWarn) handleWarn();
    }

    if (idle >= timeout) {
      doneRef.current = true;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (handleTimeout) handleTimeout();
    }
  }, []);

  // Shared by the activity listeners and `extend()`. A no-op once the hook
  // has already timed out — the hook is done until re-enabled/remounted.
  const resetActivity = useCallback(() => {
    if (doneRef.current) return;
    const { timeoutMs: timeout, now: getNow } = optsRef.current;
    lastActivityRef.current = getNow();
    if (warnedRef.current) {
      warnedRef.current = false;
      setWarned(false);
    }
    setRemainingMs(timeout);
  }, []);

  const extend = useCallback(() => {
    resetActivity();
  }, [resetActivity]);

  useEffect(() => {
    if (!enabled) return undefined;

    // Fresh start whenever the hook (re-)installs — relevant when `enabled`
    // flips back on after being off.
    doneRef.current = false;
    warnedRef.current = false;
    lastActivityRef.current = optsRef.current.now();
    setWarned(false);
    setRemainingMs(optsRef.current.timeoutMs);

    const handleActivity = () => resetActivity();

    const handleRevalidate = () => {
      recompute();
      const { onRevalidate: handleOnRevalidate } = optsRef.current;
      if (handleOnRevalidate) handleOnRevalidate();
    };

    const handleVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      handleRevalidate();
    };

    ACTIVITY_EVENTS.forEach((type) =>
      window.addEventListener(type, handleActivity, {
        passive: true,
        capture: CAPTURE_EVENTS.has(type),
      })
    );
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('focus', handleRevalidate);
    intervalRef.current = setInterval(recompute, TICK_MS);

    return () => {
      ACTIVITY_EVENTS.forEach((type) =>
        window.removeEventListener(type, handleActivity, { capture: CAPTURE_EVENTS.has(type) })
      );
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', handleRevalidate);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [enabled, recompute, resetActivity]);

  // Disabling leaves `warned`/`remainingMs` at whatever they were the
  // instant the listeners above come off, which is stale once the hook is
  // inert. This is a separate effect, keyed only on `enabled`, specifically
  // so the reset lives in an effect *setup* body rather than in the cleanup
  // above: setup functions only ever run while the component is mounted,
  // so there's no unmount-vs-disable ambiguity to guard against (unlike a
  // cleanup function, which also runs on unmount) and no need for a
  // mounted ref.
  useEffect(() => {
    if (enabled) return;
    doneRef.current = false;
    warnedRef.current = false;
    setWarned(false);
    setRemainingMs(optsRef.current.timeoutMs);
  }, [enabled]);

  return { extend, warned, remainingMs };
}

export default useIdleTimeout;
